import { DecimalPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AccountService } from '../../../../Services/Accounts/account.service';
import { FirmService } from '../../../../Services/firm.service';
import { FirmSelectionService } from '../../../../Services/firm-selection.service';
import { Subscription } from 'rxjs';
import * as XLSX from 'xlsx';
import FileSaver from 'file-saver';

interface TrialBalanceRow {
  account: string;
  openingAmount: number;
  openingType: 'DR' | 'CR';
  debit: number;
  credit: number;
  acc_id?: number;
}

@Component({
  selector: 'app-trail-balance',
  imports: [NgFor, NgClass, DecimalPipe, FormsModule, NgIf],
  templateUrl: './trail-balance.component.html',
  styleUrl: './trail-balance.component.css',
})
export class TrailBalanceComponent implements OnInit, OnDestroy {
  firms: any[] = [];
  selectedFirmId: any = '';
  selectedFirmName: string = 'All Firms';
  fromDate: string = '';
  toDate: string = '';

  trialBalance: TrialBalanceRow[] = [];
  paginatedTrialBalance: TrialBalanceRow[] = [];

  // Pagination & Search
  p: number = 1;
  itemsPerPage: number = 10;
  totalPages: number = 0;
  searchTerm: string = '';
  activeTab: 'cash' | 'metal' = 'cash';
  isNonGst: boolean = false;

  private firmSubscription: Subscription | null = null;

  constructor(
    private accountService: AccountService,
    private firmService: FirmService,
    private firmSelectionService: FirmSelectionService
  ) { }

  ngOnInit() {
    this.loadFirms();

    // Subscribe to global firm selection for default and restriction
    this.firmSubscription = this.firmSelectionService.selectedFirmName$.subscribe(firm => {
      if (firm) {
        this.selectedFirmId = firm.id;
        // Re-filter firms just in case
        this.loadFirms();
        this.loadTrialBalance();
      }
    });

    // Initial check from service
    const currentFirm = this.firmSelectionService.getselectedFirmName();
    if (currentFirm) {
      this.selectedFirmId = currentFirm.id;
      this.setFirmName();
      this.loadTrialBalance();
    }
  }

  ngOnDestroy(): void {
    if (this.firmSubscription) {
      this.firmSubscription.unsubscribe();
    }
  }


  loadFirms() {
    this.firmService.getFirms().subscribe({
      next: (res: any) => {
        const allFirms = res.data || [];
        const currentFirm = this.firmSelectionService.getselectedFirmName();

        if (currentFirm) {
          // Filter to show only the logged-in/selected firm
          this.firms = allFirms.filter((f: any) => f.id == currentFirm.id);
          if (this.firms.length > 0) {
            this.selectedFirmId = this.firms[0].id;
          }
        } else {
          this.firms = allFirms;
        }

        if (this.selectedFirmId) {
          this.setFirmName();
        }
      },
      error: (err) => console.error('Error loading firms', err),
    });
  }

  loadTrialBalance() {
    const params: any = {};

    if (this.selectedFirmId) {
      params.firm_id = this.selectedFirmId;
    }
    if (this.fromDate) params.from_date = this.fromDate;
    if (this.toDate) params.to_date = this.toDate;

    this.accountService.getTrialBalance(params).subscribe({
      next: (res: any) => {
        const data = res.data || [];
        // Sort alphabetically by account name
        this.trialBalance = data.sort((a: any, b: any) =>
          a.account.toLowerCase().localeCompare(b.account.toLowerCase())
        );
        this.p = 1;
        this.filterTable();
      },
      error: (err) => console.error('Error loading trial balance', err),
    });
  }

  filterTable() {
    this.isNonGst = this.isNonGstFirm();

    let filtered = this.trialBalance;

    // Apply Metal/Cash filter for Non-GST firms
    if (this.isNonGst) {
      filtered = this.trialBalance.filter(row => {
        const isMetal = row.account.toLowerCase().includes('metal');
        return this.activeTab === 'metal' ? isMetal : !isMetal;
      });
    }

    if (this.searchTerm) {
      const search = this.searchTerm.toLowerCase();
      filtered = filtered.filter(row =>
        row.account.toLowerCase().includes(search)
      );
    }

    this.totalPages = Math.ceil(filtered.length / this.itemsPerPage);
    if (this.p > this.totalPages && this.totalPages > 0) {
      this.p = this.totalPages;
    }

    const start = (this.p - 1) * this.itemsPerPage;
    this.paginatedTrialBalance = filtered.slice(start, start + this.itemsPerPage);
  }

  isNonGstFirm(): boolean {
    const firm = this.firmSelectionService.getselectedFirmName();
    if (!firm) return false;
    return !this.firmSelectionService.isGstFirm(firm.status, firm.name);
  }

  switchTab(tab: 'cash' | 'metal') {
    this.activeTab = tab;
    this.p = 1;
    this.filterTable();
  }

  onPageChange(page: number) {
    this.p = page;
    this.filterTable();
  }

  onSearchChange() {
    this.p = 1;
    this.filterTable();
  }

  get pageNumbers(): (number | string)[] {
    const totalPages = this.totalPages;
    const currentPage = this.p;
    const maxPagesToShow = 5;
    const pages: (number | string)[] = [];

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) {
        pages.push('...');
      }
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 2) {
        startPage = 2;
        endPage = 3;
      }
      if (currentPage >= totalPages - 1) {
        startPage = totalPages - 2;
        endPage = totalPages - 1;
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      pages.push(totalPages);
    }
    return pages;
  }

  goToPreviousPage() {
    if (this.p > 1) {
      this.onPageChange(this.p - 1);
    }
  }

  goToNextPage() {
    if (this.p < this.totalPages) {
      this.onPageChange(this.p + 1);
    }
  }

  onFilterChange() {
    this.setFirmName();
    this.loadTrialBalance();
  }

  setFirmName() {
    if (this.selectedFirmId) {
      const firm = this.firms.find(f => f.id == this.selectedFirmId);
      this.selectedFirmName = firm ? firm.name : 'Unknown Firm';
    } else {
      this.selectedFirmName = 'All Firms';
    }
  }

  getClosing(row: TrialBalanceRow) {
    let balance =
      row.openingType === 'DR'
        ? row.openingAmount + row.debit - row.credit
        : row.credit + row.openingAmount - row.debit;

    return {
      amount: Math.abs(balance),
      type:
        balance >= 0
          ? row.openingType
          : row.openingType === 'DR'
            ? 'CR'
            : 'DR',
    };
  }

  formatAmount(amount: number, type?: 'DR' | 'CR'): string {
    if (!amount) return '0.00';
    return `${amount.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
    })} ${type ?? ''}`;
  }

  trackByAccount(index: number, item: TrialBalanceRow) {
    return item.acc_id || item.account;
  }

  getTotalDebit(): number {
    const filtered = this.getCurrentlyFilteredPool();
    return filtered.reduce((sum, r) => sum + r.debit, 0);
  }

  getTotalCredit(): number {
    const filtered = this.getCurrentlyFilteredPool();
    return filtered.reduce((sum, r) => sum + r.credit, 0);
  }

  private getCurrentlyFilteredPool(): TrialBalanceRow[] {
    if (!this.isNonGst) return this.trialBalance;
    return this.trialBalance.filter(row => {
      const isMetal = row.account.toLowerCase().includes('metal');
      return this.activeTab === 'metal' ? isMetal : !isMetal;
    });
  }

  /** Net Opening Balance */
  getTotalOpening() {
    const filtered = this.getCurrentlyFilteredPool();
    const net = filtered.reduce((sum, r) => {
      return r.openingType === 'DR'
        ? sum + r.openingAmount
        : sum - r.openingAmount;
    }, 0);

    return {
      amount: Math.abs(net),
      type: net >= 0 ? 'DR' : 'CR',
    };
  }

  /** Net Closing Balance */
  getTotalClosing() {
    const filtered = this.getCurrentlyFilteredPool();
    const net = filtered.reduce((sum, r) => {
      const closing =
        r.openingType === 'DR'
          ? r.openingAmount + r.debit - r.credit
          : r.credit + r.openingAmount - r.debit;

      return (
        sum +
        (closing >= 0
          ? r.openingType === 'DR'
            ? closing
            : -closing
          : r.openingType === 'DR'
            ? -Math.abs(closing)
            : Math.abs(closing))
      );
    }, 0);

    return {
      amount: Math.abs(net),
      type: net >= 0 ? 'DR' : 'CR',
    };
  }

  printTrialBalance() {
    const data = this.getCurrentlyFilteredPool();
    const tabName = this.isNonGst ? (this.activeTab === 'metal' ? ' (Metal)' : ' (Cash)') : '';
    const fromDateStr = this.fromDate ? new Date(this.fromDate).toLocaleDateString('en-GB') : 'All';
    const toDateStr = this.toDate ? new Date(this.toDate).toLocaleDateString('en-GB') : 'To Date';
    const generatedOn = new Date().toLocaleString('en-GB');

    const totalOpening = this.getTotalOpening();
    const totalDebit = this.getTotalDebit();
    const totalCredit = this.getTotalCredit();
    const totalClosing = this.getTotalClosing();

    let printContent = `
    <html>
    <head>
      <title>Trial Balance${tabName} - ${this.selectedFirmName}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; color: #333; }
        .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #0D5BAB; padding-bottom: 10px; }
        .header h2 { margin: 0; color: #0D5BAB; text-transform: uppercase; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 20px; }
        .info-item { font-weight: bold; }
        .info-item span { font-weight: normal; }
        
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f2f2f2; font-weight: bold; border-top: 2px solid #0D5BAB; color: #0D5BAB; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        
        .footer-summary { margin-left: auto; width: 100%; border-top: 2px solid #C9AB04; background-color: #F9E576AD; }
        .total-row { font-weight: bold; }

        @media print {
          @page { size: auto; margin: 10mm; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h2>Trial Balance Report${tabName}</h2>
        <p>Firm: ${this.selectedFirmName}</p>
      </div>

      <div class="info-grid">
        <div class="info-item">Period: <span>${fromDateStr} to ${toDateStr}</span></div>
        <div class="info-item">Generated On: <span>${generatedOn}</span></div>
      </div>

      <table>
        <thead>
          <tr>
            <th>Account Details</th>
            <th class="text-right">Opening Balance</th>
            <th class="text-right">Debit Amount</th>
            <th class="text-right">Credit Amount</th>
            <th class="text-right">Closing Balance</th>
          </tr>
        </thead>
        <tbody>
    `;

    if (data.length === 0) {
      printContent += `
        <tr>
          <td colspan="5" class="text-center">No Data Available</td>
        </tr>
      `;
    } else {
      data.forEach((row) => {
        const closing = this.getClosing(row);
        printContent += `
          <tr>
            <td>${row.account}</td>
            <td class="text-right">${row.openingAmount.toFixed(2)} ${row.openingType}</td>
            <td class="text-right">${row.debit > 0 ? row.debit.toFixed(2) : '0.00'}</td>
            <td class="text-right">${row.credit > 0 ? row.credit.toFixed(2) : '0.00'}</td>
            <td class="text-right">${closing.amount.toFixed(2)} ${closing.type}</td>
          </tr>
        `;
      });
    }

    printContent += `
        </tbody>
        <tfoot class="footer-summary">
          <tr class="total-row">
            <td>Total</td>
            <td class="text-right">${totalOpening.amount.toFixed(2)} ${totalOpening.type}</td>
            <td class="text-right">${totalDebit.toFixed(2)}</td>
            <td class="text-right">${totalCredit.toFixed(2)}</td>
            <td class="text-right">${totalClosing.amount.toFixed(2)} ${totalClosing.type}</td>
          </tr>
        </tfoot>
      </table>

      <script>
        window.onload = function() {
          window.print();
          window.onafterprint = function() { window.close(); };
        }
      </script>
    </body>
    </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();
    }
  }

  exportToExcel() {
    const data = this.getCurrentlyFilteredPool();
    const tabTitle = this.isNonGst ? (this.activeTab === 'metal' ? ' (Metal)' : ' (Cash)') : '';

    if (data.length === 0) {
      alert('No data available to export');
      return;
    }

    // Map data to a numeric-friendly structure
    const exportData = data.map((row) => {
      const closing = this.getClosing(row);
      return {
        'Account Details': row.account,
        'Opening Balance': this.formatAmount(row.openingAmount, row.openingType),
        'Debit Amount': row.debit,
        'Credit Amount': row.credit,
        'Closing Balance': this.formatAmount(closing.amount, closing.type),
      };
    });

    // Add Totals row
    const totalOpening = this.getTotalOpening();
    const totalDebit = this.getTotalDebit();
    const totalCredit = this.getTotalCredit();
    const totalClosing = this.getTotalClosing();

    exportData.push({
      'Account Details': 'Total',
      'Opening Balance': this.formatAmount(totalOpening.amount, totalOpening.type as 'DR' | 'CR'),
      'Debit Amount': totalDebit,
      'Credit Amount': totalCredit,
      'Closing Balance': this.formatAmount(totalClosing.amount, totalClosing.type as 'DR' | 'CR'),
    });

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);

    // Set column widths for better readability
    worksheet['!cols'] = [
      { wch: 30 }, // Account
      { wch: 20 }, // Opening Balance
      { wch: 15 }, // Debit
      { wch: 15 }, // Credit
      { wch: 20 }, // Closing Balance
    ];

    const workbook: XLSX.WorkBook = {
      Sheets: { 'Trial Balance': worksheet },
      SheetNames: ['Trial Balance'],
    };

    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const fileName = `Trial_Balance${tabTitle.replace(/\s+/g, '_')}_${this.selectedFirmName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    const dataBlob: Blob = new Blob([excelBuffer], {
      type: 'application/octet-stream',
    });
    FileSaver.saveAs(dataBlob, fileName);
  }
}
